<script setup lang="ts">
import { computed } from 'vue'
import { formatName } from '@/utils/formatName'
import { formatStartDate } from '@/utils/formatStartDate'

/**
 * Client name cell: shows the client name on the first line and, when at
 * least one piece of secondary metadata is provided, folds the (now
 * column-less) gender and start date into a single muted meta line
 * underneath.
 *
 * Layout (with metadata):
 *   <name>
 *   <gender letter>, <duration since start, e.g. "30 days ago">
 *
 * Layout (no metadata, e.g. DiseasesTab where neither gender nor start
 * date is available): only the name line is rendered, so the cell
 * collapses to a single line. The meta line is intentionally hidden
 * rather than filled with a placeholder so callers without secondary
 * data don't have to render a "–" stub. This lets unrelated tabs share
 * the same name-cell typography (font family, size, weight) as
 * `ClientsTab` without paying for a meaningless secondary line.
 *
 * The exact formatted start date is intentionally omitted; the relative
 * duration ("about 9 hours ago", "30 days ago") is enough in the table.
 * When only gender is provided the comma + duration are dropped and the
 * meta line shows just the gender letter.
 *
 * The meta line is rendered with `text-tufte-muted` (same muted grey as the
 * bottom line of `StartDateCell` / `DateTimeCell`), so the block reads as
 * secondary metadata beside the primary name.
 */
const props = withDefaults(
  defineProps<{
    /** Client display name; empty/blank renders as blank. */
    name?: string
    /** Gender letter (`m` / `f`) or full word; unknown/blank renders as en dash. */
    gender?: string | null
    /** Start date (Date instance or ISO string). Omit: meta line shows only gender. */
    startDate?: Date | string
    /** Locale for start-date formatting. Default from config. */
    locale?: string
    /** Intl dateStyle passed to `formatStartDate` (not shown directly; only the relative duration is displayed). */
    dateStyle?: 'long' | 'short' | 'medium'
    /** IANA timezone for start-date display. Undefined = local timezone. */
    timeZone?: string
    /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
    maxWidth?: string
  }>(),
  { locale: undefined, dateStyle: undefined, timeZone: undefined },
)

const formattedName = computed(() => formatName(props.name))

/**
 * True when the caller actually passed a `gender` prop (any value including
 * the empty string or an unrecognized letter); false when the prop is
 * `undefined`/`null`. Used to decide whether the meta line is even
 * relevant — a tab that has no notion of gender (e.g. DiseasesTab) should
 * not get a "–" placeholder it never asked for.
 */
const hasGenderProvided = computed(() => props.gender !== undefined && props.gender !== null)

/**
 * Gender letter derived from the first character of `props.gender` (upstream
 * `genderLetter()` already normalizes it to `m` / `f`). When the prop is
 * provided but unknown/blank we still surface an en dash so the meta line
 * has something to show alongside a valid start date.
 */
const genderLabel = computed(() => {
  const raw = props.gender
  if (typeof raw !== 'string' || raw.trim() === '') return '–'
  const ch = raw.trim().charAt(0).toLowerCase()
  if (ch === 'm' || ch === 'f') return ch
  return '–'
})

const hasStartDate = computed(
  () =>
    props.startDate !== undefined &&
    props.startDate !== null &&
    (typeof props.startDate !== 'string' || props.startDate.trim() !== ''),
)

const durationSince = computed(() =>
  hasStartDate.value
    ? formatStartDate({
        date: props.startDate!,
        locale: props.locale,
        dateStyle: props.dateStyle,
        timeZone: props.timeZone,
      }).durationSince
    : '',
)

/**
 * Whether the secondary meta line should be rendered at all. We only show
 * it when the caller provided at least one of `gender` or `startDate`;
 * otherwise the cell collapses to a single name line (used by tabs like
 * DiseasesTab that share the typography but have no metadata).
 */
const hasMeta = computed(() => hasGenderProvided.value || hasStartDate.value)

/**
 * Full meta-line text: "m, 30 days ago" when both tokens are present, just
 * the gender letter when only gender was provided, or the bare duration
 * when only the start date was provided. Returns an empty string when
 * `hasMeta` is false; in that case the meta `<span>` is not rendered.
 */
const metaText = computed(() => {
  if (!hasMeta.value) return ''
  const gender = hasGenderProvided.value ? genderLabel.value : ''
  const duration = durationSince.value
  if (gender && duration) return `${gender}, ${duration}`
  return gender || duration
})
</script>

<template>
  <td
    class="client-name-cell text-tufte-text font-tufte"
    :style="props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <div class="flex flex-col gap-0.5">
      <span>{{ formattedName }}</span>
      <span v-if="hasMeta" class="client-name-cell__meta text-tufte-muted">{{ metaText }}</span>
    </div>
  </td>
</template>

<style scoped>
/*
 * The primary name line is rendered one step larger than the table's
 * default `--text-tufte-sm` body size so names stand out as the row's
 * anchor. The meta line (gender + relative start date) uses
 * `--text-tufte-xs` to match the secondary line of every other two-line
 * cell (`LastVisitCell`, `StartDateCell`, `DateTimeCell`,
 * `TwoLineTextCell`), keeping the bottom-line typography consistent
 * across the table.
 */
.client-name-cell {
  font-size: var(--text-tufte-lg);
}

.client-name-cell__meta {
  font-size: var(--text-tufte-xs);
  line-height: var(--line-height-tufte-normal);
  white-space: nowrap;
}
</style>
